import { format } from "date-fns"

import type { DayStats } from "@/hooks/useMonthlyShiftStats"
import type { Ticket } from "@/hooks/useTickets"
import { ukFormat } from "@/lib/ukFormat"
import type { ApplicationExportRow } from "@/services/fetchApplicationsForExport"
import type { Shift } from "@/services/shiftsService"

function quoteCSVCell(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function downloadCSV(headers: string[], rows: string[][], filename: string): void {
  const csvContent = [
    headers.map(quoteCSVCell).join(","),
    ...rows.map((row) => row.map(quoteCSVCell).join(",")),
  ].join("\n")

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const link = document.createElement("a")
  const url = URL.createObjectURL(blob)
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

interface HRPersonStats {
  person: string
  numberOfShifts: number
  latenessCount: number
  latenessPercent: number
  awolCount: number
  awolPercent: number
  scheduledHours: number
  clockedHours: number
  autoClockOutCount: number
  autoClockOutPercent: number
}

export function exportHRStatsToCSV(
  teamStats: { team: string; people: HRPersonStats[] }[],
  filename: string,
): void {
  if (teamStats.length === 0) return

  const headers = [
    "team",
    "person",
    "numberOfBlocks",
    "latenessCount",
    "latenessPercent",
    "awolCount",
    "awolPercent",
    "scheduledHours",
    "clockedHours",
    "autoClockOutCount",
    "autoClockOutPercent",
  ]

  const rows: string[][] = []
  teamStats.forEach((teamData) => {
    teamData.people.forEach((person) => {
      rows.push([
        teamData.team,
        person.person,
        person.numberOfShifts.toString(),
        person.latenessCount.toString(),
        person.latenessPercent.toFixed(1),
        person.awolCount.toString(),
        person.awolPercent.toFixed(1),
        person.scheduledHours.toFixed(1),
        person.clockedHours.toFixed(1),
        person.autoClockOutCount.toString(),
        person.autoClockOutPercent.toFixed(1),
      ])
    })
  })

  downloadCSV(headers, rows, filename)
}

export function exportTicketsToCSV(tickets: Ticket[], filename: string): void {
  if (tickets.length === 0) return

  const headers = [
    "person",
    "dsp",
    "ticket_name",
    "category",
    "sub_category",
    "details",
    "ticket_date_time",
    "amount",
    "created_by",
    "updated_by",
    "updated_at",
    "created_at",
    "tracking_id",
    "status",
    "route_code",
    "route_cycle",
  ]

  const escape = (value: unknown): string => {
    if (value === null || value === undefined) return ""
    return String(value)
  }

  const rows = tickets.map((t) => [
    escape(t.person),
    escape(t.dsp),
    escape(t.ticket_name),
    escape(t.category),
    escape(t.sub_category),
    escape(t.details),
    escape(t.ticket_date_time),
    escape(t.amount),
    escape(t.created_by),
    escape(t.updated_by),
    escape(t.updated_at),
    escape(t.created_at),
    escape(t.tracking_id),
    escape(t.status),
    escape(t.route_code),
    escape(t.route_cycle),
  ])

  downloadCSV(headers, rows, filename)
}

export function exportMonthlyStatsToCSV(dayStats: DayStats[], filename: string): void {
  if (dayStats.length === 0) return

  const headers = ["date", "planned", "scheduled", "unfulfilled", "cancelled"]

  const rows = dayStats.map((stat) => [
    stat.date,
    stat.total.toString(),
    stat.filled.toString(),
    stat.unfulfilled.toString(),
    stat.cancelled.toString(),
  ])

  downloadCSV(headers, rows, filename)
}

export function exportShiftsToCSV(shifts: Shift[], filename?: string): void {
  if (shifts.length === 0) return

  const headers = [
    "date",
    "startTime",
    "endTime",
    "startTimeFull",
    "endTimeFull",
    "status",
    "area",
    "type",
    "employer",
    "location",
    "preassignedRoute",
    "notes",
    "person",
    "minutesLate",
    "awol",
    "cancelledReason",
    "cancelledTime",
    "clockedInTime",
    "clockedOutTime",
    "timesheetBreak",
  ]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const escapeCSV = (value: any): string => {
    if (value === null || value === undefined) return ""
    return String(value).replace(/"/g, '""')
  }

  const formatDateTime = (dateString: string | null | undefined, formatStr: string): string => {
    if (!dateString) return ""
    try {
      return ukFormat(dateString, formatStr)
    } catch {
      return dateString
    }
  }

  const rows = shifts.map((shift) => {
    const startDate = new Date(shift.startTime)
    const endDate = new Date(shift.endTime)

    return [
      ukFormat(startDate, "yyyy-MM-dd"),
      ukFormat(startDate, "HH:mm"),
      ukFormat(endDate, "HH:mm"),
      ukFormat(startDate, "yyyy-MM-dd HH:mm:ss"),
      ukFormat(endDate, "yyyy-MM-dd HH:mm:ss"),
      shift.status || "",
      shift.area || "",
      shift.type || "",
      shift.employer || "",
      shift.location || "",
      shift.preassignedRoute || "",
      escapeCSV(shift.notes),
      shift.people?.person || "",
      shift.minutesLate?.toString() || "",
      shift.awol ? "Yes" : "No",
      shift.cancelledReason || "",
      formatDateTime(shift.cancelledTime, "yyyy-MM-dd HH:mm:ss"),
      formatDateTime(shift.timesheet?.clockedInTime, "yyyy-MM-dd HH:mm:ss"),
      formatDateTime(shift.timesheet?.clockedOutTime, "yyyy-MM-dd HH:mm:ss"),
      shift.timesheet?.break?.toString() || "",
    ]
  })

  downloadCSV(
    headers,
    rows,
    filename || `shifts_${format(new Date(), "yyyy-MM-dd")}_${shifts.length}.csv`,
  )
}

export function exportApplicationsToCSV(applications: ApplicationExportRow[], filename: string): void {
  if (applications.length === 0) return

  const headers = [
    "Application ID",
    "Name",
    "Location",
    "Current Stage",
    "Comments",
    "Email",
    "Telephone Number",
  ]

  const rows = applications.map((a) => [
    a.applicationId,
    a.name,
    a.location,
    a.stage,
    a.comments,
    a.email,
    a.phone,
  ])

  downloadCSV(headers, rows, filename)
}
